import { DataService } from "@/services/data";
import { useEffect, useState } from "react";
import { ANY_RECORD_PREFIX, type FilterSchema, type ResolvedPath } from "./types";

// One request per model and session: the builder opens a relation, the schema of its target
// loads once and every picker reuses it.
const cache = new Map<string, Promise<FilterSchema>>();

export function loadFilterSchema(model: string): Promise<FilterSchema> {
    let pending = cache.get(model);
    if (!pending) {
        pending = DataService.client.url(`/crm/app/filter-schema/${model}/`).get().json<FilterSchema>();
        pending.catch(() => cache.delete(model));
        cache.set(model, pending);
    }
    return pending;
}

export function resetFilterSchemaCache(): void {
    cache.clear();
}

export function useFilterSchema(model?: string | null) {
    const [state, setState] = useState<{ schema: FilterSchema | null; loading: boolean; error: string | null }>({
        schema: null,
        loading: Boolean(model),
        error: null
    });
    useEffect(() => {
        if (!model) {
            setState({ schema: null, loading: false, error: null });
            return;
        }
        let cancelled = false;
        // Never keep the previous model's schema while the next loads: its fields would be
        // offered under the new model's path.
        setState({ schema: null, loading: true, error: null });
        loadFilterSchema(model)
            .then(schema => !cancelled && setState({ schema, loading: false, error: null }))
            .catch(() => !cancelled && setState({ schema: null, loading: false, error: "Failed to load the filterable fields." }));
        return () => {
            cancelled = true;
        };
    }, [model]);
    return state;
}

/**
 * Follow `path` (schema names joined by `__`) from `rootModel`: every segment but the last is a
 * to-one relation; the last is a field, a to-one relation (tested as a record) or, with
 * `related`, a to-many relation.
 */
export async function resolvePath(rootModel: string, path: string, related = false): Promise<ResolvedPath | null> {
    if (!path) return null;
    if (related && path.startsWith(ANY_RECORD_PREFIX)) {
        // Every record of an object, not related ones: the block reads « Any opportunity ».
        const target = await loadFilterSchema(path.slice(ANY_RECORD_PREFIX.length));
        const relation = { name: path, label: `Any ${target.name.toLowerCase()}`, kind: "to_many" as const, model: target.model };
        return { labels: [relation.label], schema: target, relation };
    }
    const segments = path.split("__");
    let schema = await loadFilterSchema(rootModel);
    const labels: string[] = [];
    for (const name of segments.slice(0, -1)) {
        const hop = schema.relations.find(relation => relation.name === name && relation.kind === "to_one");
        if (!hop) return null;
        labels.push(hop.label);
        schema = await loadFilterSchema(hop.model);
    }
    const last = segments[segments.length - 1];
    if (related) {
        const relation = schema.relations.find(candidate => candidate.name === last && candidate.kind === "to_many");
        return relation ? { labels: [...labels, relation.label], schema, relation } : null;
    }
    const field = schema.fields.find(candidate => candidate.name === last);
    if (field) return { labels: [...labels, field.label], schema, field };
    const relation = schema.relations.find(candidate => candidate.name === last && candidate.kind === "to_one");
    if (relation) {
        return {
            labels: [...labels, relation.label],
            schema,
            relation,
            field: {
                name: relation.name,
                label: relation.label,
                type: "record",
                operators: relation.operators ?? ["in", "not_in", "is_empty", "is_not_empty"],
                formula_operators: relation.formula_operators
            }
        };
    }
    return null;
}

export function useResolvedPath(rootModel: string, path: string, related = false) {
    const [resolved, setResolved] = useState<ResolvedPath | null | undefined>(undefined);
    useEffect(() => {
        let cancelled = false;
        if (!path) {
            setResolved(null);
            return;
        }
        setResolved(undefined);
        resolvePath(rootModel, path, related)
            .then(result => !cancelled && setResolved(result))
            .catch(() => !cancelled && setResolved(null));
        return () => {
            cancelled = true;
        };
    }, [rootModel, path, related]);
    return resolved;
}

export type FilterSchemaIdentity = Pick<FilterSchema, "model" | "name" | "name_plural" | "icon" | "color" | "object_url">;

let objectsCache: Promise<FilterSchemaIdentity[]> | null = null;

/** The objects a block can probe every record of (`GET /crm/app/filter-objects/`), loaded once. */
export function useFilterObjects(enabled: boolean): FilterSchemaIdentity[] | null {
    const [objects, setObjects] = useState<FilterSchemaIdentity[] | null>(null);
    useEffect(() => {
        if (!enabled) return;
        if (!objectsCache) {
            objectsCache = DataService.client.url("/crm/app/filter-objects/").get().json<FilterSchemaIdentity[]>();
            objectsCache.catch(() => (objectsCache = null));
        }
        let cancelled = false;
        objectsCache.then(next => !cancelled && setObjects(next)).catch(() => !cancelled && setObjects([]));
        return () => {
            cancelled = true;
        };
    }, [enabled]);
    return objects;
}
